import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';

const GlobalSearchModal = ({
  isOpen,
  onClose,
  token,
  apiUrl,
  tenantName = 'Your Organization',
  onSelectTicket = () => {},
  onSelectAsset = () => {}
}) => {
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'tickets', 'assets'
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ tickets: [], assets: [], totalCount: 0 });
  const inputRef = useRef(null);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (inputRef.current) inputRef.current.focus();
      }, 50);
    } else {
      setQuery('');
      setResults({ tickets: [], assets: [], totalCount: 0 });
    }
  }, [isOpen]);

  // Debounced search effect
  useEffect(() => {
    if (!isOpen) return;
    
    const trimmed = query.trim();
    if (!trimmed) {
      setResults({ tickets: [], assets: [], totalCount: 0 });
      setLoading(false);
      return;
    }

    setLoading(true);
    const timeoutId = setTimeout(async () => {
      try {
        const res = await fetch(`${apiUrl}/api/dashboard/global-search?q=${encodeURIComponent(trimmed)}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (res.ok) {
          const data = await res.json();
          setResults(data);
        } else {
          setResults({ tickets: [], assets: [], totalCount: 0 });
        }
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [query, isOpen, apiUrl, token]);

  if (!isOpen) return null;

  const tickets = results.tickets || [];
  const assets = results.assets || [];

  const displayTickets = activeTab === 'assets' ? [] : tickets;
  const displayAssets = activeTab === 'tickets' ? [] : assets;
  const hasResults = displayTickets.length > 0 || displayAssets.length > 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-xs flex items-start justify-center pt-12 md:pt-20 px-4">
      {/* Backdrop click */}
      <div className="fixed inset-0" onClick={onClose} />

      {/* Search Dialog Card */}
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-10 flex flex-col max-h-[80vh] animate-fadeIn">
        {/* Top Input Bar */}
        <div className="p-4 border-b border-slate-100 flex items-center gap-3 bg-white">
          <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          <input
            ref={inputRef}
            type="text"
            className="flex-1 bg-transparent text-slate-800 placeholder-slate-400 text-sm md:text-base font-medium focus:outline-none"
            placeholder="Search ticket number, title, asset name, serial number..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
            }}
          />

          {loading && (
            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin flex-shrink-0" />
          )}

          {query && !loading && (
            <button
              onClick={() => setQuery('')}
              className="text-slate-400 hover:text-slate-600 p-1 rounded-md text-xs font-bold"
            >
              ✕
            </button>
          )}

          <button
            onClick={onClose}
            className="text-[11px] font-semibold text-slate-400 bg-slate-100 hover:bg-slate-200 border border-slate-200 px-2 py-1 rounded-lg transition-colors"
          >
            ESC
          </button>
        </div>

        {/* Tab Filters (When results exist) */}
        {query.trim() && (
          <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2 text-xs">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-3 py-1 rounded-full font-semibold transition-colors ${
                activeTab === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-600 hover:bg-slate-200/70'
              }`}
            >
              All ({results.totalCount || 0})
            </button>
            <button
              onClick={() => setActiveTab('tickets')}
              className={`px-3 py-1 rounded-full font-semibold transition-colors ${
                activeTab === 'tickets'
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-600 hover:bg-slate-200/70'
              }`}
            >
              Tickets ({tickets.length})
            </button>
            <button
              onClick={() => setActiveTab('assets')}
              className={`px-3 py-1 rounded-full font-semibold transition-colors ${
                activeTab === 'assets'
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-600 hover:bg-slate-200/70'
              }`}
            >
              Assets ({assets.length})
            </button>
          </div>
        )}

        {/* Results Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* 1. Empty query prompt */}
          {!query.trim() && (
            <div className="py-10 text-center text-slate-400">
              <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-500 flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-slate-700">Quick Global Search</p>
              <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                Type a ticket number, title, description, asset tag, or serial number to search within your tenant organization.
              </p>
            </div>
          )}

          {/* 2. No results found */}
          {query.trim() && !loading && !hasResults && (
            <div className="py-10 text-center text-slate-400">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-slate-700">No results found</p>
              <p className="text-xs text-slate-400 mt-1">
                No tickets or assets matching <span className="text-slate-600 font-bold">"{query}"</span> found in your tenant workspace.
              </p>
            </div>
          )}

          {/* 3. Tickets Results */}
          {displayTickets.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Tickets ({displayTickets.length})
                </span>
              </div>
              <div className="space-y-2">
                {displayTickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    onClick={() => {
                      onSelectTicket(ticket);
                      onClose();
                    }}
                    className="p-3 bg-slate-50 hover:bg-blue-50/70 border border-slate-100 hover:border-blue-200 rounded-xl cursor-pointer transition-all duration-150 flex items-center justify-between group"
                  >
                    <div className="flex items-start space-x-3 min-w-0">
                      <div className={`w-2.5 h-2.5 mt-1.5 rounded-full flex-shrink-0 ${
                        ticket.priority === 'high' ? 'bg-red-500 shadow-xs shadow-red-200' :
                        ticket.priority === 'medium' ? 'bg-amber-500 shadow-xs shadow-amber-200' : 'bg-emerald-500 shadow-xs shadow-emerald-200'
                      }`} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-mono font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                            #{ticket.id ? ticket.id.slice(0, 8) : 'TKT'}
                          </span>
                          <span className="text-sm font-bold text-slate-800 group-hover:text-blue-600 transition-colors truncate">
                            {ticket.title}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 truncate mt-1">
                          {ticket.description || 'No description provided'}
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-400">
                          <span>by {ticket.created_by_username || 'User'}</span>
                          {ticket.created_at && (
                            <>
                              <span>•</span>
                              <span>{format(new Date(ticket.created_at), 'MMM dd, yyyy')}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        ticket.status === 'open' ? 'bg-red-50 text-red-600 border border-red-100' :
                        ticket.status === 'in_progress' ? 'bg-amber-50 text-amber-600 border border-amber-100' :
                        'bg-emerald-50 text-emerald-600 border border-emerald-100'
                      }`}>
                        {ticket.status === 'open' ? 'Open' : ticket.status === 'in_progress' ? 'In Progress' : 'Closed'}
                      </span>
                      <svg className="w-4 h-4 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 4. Assets Results */}
          {displayAssets.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Assets ({displayAssets.length})
                </span>
              </div>
              <div className="space-y-2">
                {displayAssets.map((asset) => (
                  <div
                    key={asset.id}
                    onClick={() => {
                      onSelectAsset(asset);
                      onClose();
                    }}
                    className="p-3 bg-slate-50 hover:bg-emerald-50/70 border border-slate-100 hover:border-emerald-200 rounded-xl cursor-pointer transition-all duration-150 flex items-center justify-between group"
                  >
                    <div className="flex items-start space-x-3 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold text-slate-800 group-hover:text-emerald-700 transition-colors truncate">
                            {asset.name}
                          </span>
                          <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded capitalize">
                            {asset.asset_type || 'Asset'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-400 flex-wrap">
                          <span>Serial: <strong className="text-slate-600">{asset.serial_number || 'N/A'}</strong></span>
                          <span>•</span>
                          <span>Value: <strong className="text-slate-600">₹{Number(asset.value || 0).toLocaleString('en-IN')}</strong></span>
                          {asset.assigned_to_username && (
                            <>
                              <span>•</span>
                              <span>Assigned: <strong className="text-slate-600">{asset.assigned_to_username}</strong></span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      <svg className="w-4 h-4 text-slate-400 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer: Shortcuts Notice */}
        <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
          <div className="flex items-center gap-2">
            <span>Search tickets, assets & serial numbers</span>
          </div>

          <div className="hidden sm:flex items-center gap-3">
            <span>Press <kbd className="bg-white border border-slate-200 px-1.5 py-0.5 rounded shadow-2xs font-mono text-[10px]">ESC</kbd> to exit</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalSearchModal;
